import React, { useMemo, useState } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs, doc, setDoc } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { db } from "../../services/firebase";

export default function DataMigration() {
  const [srcText, setSrcText] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState("");
  const [srcEmail, setSrcEmail] = useState("");
  const [srcPassword, setSrcPassword] = useState("");
  const [selected, setSelected] = useState({
    employees: true,
    students: true,
    payroll: false,
    uniforms_inventory: true,
    uniforms_sales: false,
    books_inventory: true,
    books_bundles: true,
    books_sales: false,
  });
  const [counts, setCounts] = useState({});
  const canRun = useMemo(() => srcText.trim().length > 0 && Object.values(selected).some(Boolean), [srcText, selected]);

  function append(msg) {
    setLog(l => l + (l ? "\n" : "") + msg);
  }

  function parseConfig() {
    try {
      const cfg = JSON.parse(srcText);
      if (!cfg.apiKey || !cfg.projectId) throw new Error("Invalid config");
      return cfg;
    } catch (e) {
      throw new Error("Invalid JSON config");
    }
  }

  async function ensureAuth(app) {
    const auth = getAuth(app);
    await signInWithEmailAndPassword(auth, srcEmail.trim(), srcPassword);
  }

  async function dryRun() {
    setBusy(true);
    setLog("");
    try {
      const cfg = parseConfig();
      const app = initializeApp(cfg, "src");
      const sdb = getFirestore(app);
      await ensureAuth(app);
      const cols = Object.keys(selected).filter(k => selected[k]);
      const c = {};
      for (const col of cols) {
        const snap = await getDocs(collection(sdb, col));
        c[col] = snap.size;
        append(`${col}: ${snap.size} docs`);
      }
      setCounts(c);
    } catch (e) {
      append(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function copyNow() {
    setBusy(true);
    setLog("");
    try {
      const cfg = parseConfig();
      const app = initializeApp(cfg, "src");
      const sdb = getFirestore(app);
      await ensureAuth(app);
      const cols = Object.keys(selected).filter(k => selected[k]);
      for (const col of cols) {
        append(`Copying ${col}...`);
        const snap = await getDocs(collection(sdb, col));
        for (const d of snap.docs) {
          await setDoc(doc(db, col, d.id), d.data(), { merge: true });
        }
        append(`Done ${col}: ${snap.size} docs`);
      }
      append("Migration complete");
    } catch (e) {
      append(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container py-4">
      <h3 className="mb-3">Data Migration (Staging → Prod)</h3>
      <div className="alert alert-info">
        Paste the source Firebase Web App JSON config. Select collections to copy. Use Dry Run to see counts, then Copy.
      </div>
      <div className="mb-3">
        <textarea className="form-control" rows={8} placeholder='{"apiKey":"...","authDomain":"...","projectId":"...","storageBucket":"...","messagingSenderId":"...","appId":"...","measurementId":"..."}' value={srcText} onChange={e => setSrcText(e.target.value)} />
      </div>
      <div className="mb-3 d-flex gap-2">
        <input className="form-control" style={{ maxWidth: 320 }} placeholder="Staging email" value={srcEmail} onChange={e => setSrcEmail(e.target.value)} />
        <input type="password" className="form-control" style={{ maxWidth: 220 }} placeholder="Staging password" value={srcPassword} onChange={e => setSrcPassword(e.target.value)} />
      </div>
      <div className="mb-3 d-flex flex-wrap gap-3">
        {Object.keys(selected).map(k => (
          <label key={k} className="form-check">
            <input type="checkbox" className="form-check-input" checked={selected[k]} onChange={e => setSelected(s => ({ ...s, [k]: e.target.checked }))} />
            <span className="form-check-label ms-2">{k}</span>
          </label>
        ))}
      </div>
      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-outline-secondary" onClick={dryRun} disabled={busy || !canRun}>Dry Run</button>
        <button className="btn btn-primary" onClick={copyNow} disabled={busy || !canRun}>Copy Selected</button>
      </div>
      <div className="card">
        <div className="card-body">
          <h6 className="mb-2">Results</h6>
          <pre style={{ whiteSpace: "pre-wrap" }}>{log || "No logs yet"}</pre>
        </div>
      </div>
    </div>
  );
}
